import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.control-albaranes-hosteleria.${field}`

export function ControlAlbaranesArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'pedido', label: t(k('toc.1'), '1. ¿Cómo saber si el proveedor ha traído lo que pediste?') },
        { id: 'pesar', label: t(k('toc.2'), '2. ¿Te están cobrando por producto que nunca ha llegado?') },
        { id: 'precios', label: t(k('toc.3'), '3. Cómo detectar antes las subidas de precio') },
        { id: 'pagar', label: t(k('toc.4'), '4. Quién controla el albarán, controla lo que paga') },
        { id: 'caso', label: t(k('toc.5'), '5. Un ejemplo con números') },
        { id: 'materia-prima', label: t(k('toc.6'), '6. Albaranes y coste de materia prima') },
        { id: 'matices', label: t(k('toc.7'), '7. Lo que el control de albaranes no arregla') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'El control de albaranes es la revisión sistemática de lo que un proveedor te factura frente a lo que realmente te entrega, y es una de las formas más directas de proteger el margen de un restaurante.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Cocinero comprobando un albarán frente a cajas de verdura y pescado en la puerta de cocina',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.1'),
                                    'Un albarán es el documento que trae el proveedor con cada pedido y que detalla qué producto, en qué cantidad y a qué precio te está facturando. Casi nadie lo mira de verdad.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.2'),
                                    'Llega el proveedor, dejas las cajas en una esquina de la cocina y sigues con el servicio. Firmas el albarán casi sin mirarlo, porque total, ya lo revisarás luego. Nos ha pasado a todos.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(
                                    k('intro.3'),
                                    'El problema es que ese papel que firmas sin mirar es dinero. Controlar bien tus albaranes no es un capricho de orden. Puede suponer cientos o miles de euros al mes, para bien o para mal.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='pedido' className={articleUi.h2}>
                        {t(k('h2.pedido'), '¿Cómo saber si el proveedor ha traído lo que pediste?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('lead.pedido'),
                                    'Comparar el albarán con el pedido original, en el momento de la entrega, es la única forma de detectar una falta antes de que te falte el producto en el servicio.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.pedido'),
                                'No hay nada más caro que darte cuenta a media comanda de que falta algo porque el proveedor te ha fallado y no te ha avisado. Revisar el albarán contra el pedido con las cajas todavía en la puerta te evita salir corriendo al súper de la esquina a pagar el triple por algo que ya habías pagado una vez.',
                            )}
                        </p>
                    </div>

                    <h2 id='pesar' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.pesar'), '¿Te están cobrando por producto que nunca ha llegado?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('lead.pesar'),
                                    'Pesar la mercancía frente a lo que indica el albarán es la única forma de saber si te están cobrando por producto que no ha llegado.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.pesar'),
                                'Pesa todo lo que puedas: cajas de género, sacos, botellas, lo que sea. Te vas a encontrar más de una sorpresa, y esas sorpresas casi siempre van en tu contra, nunca a tu favor. Un kilo de menos aquí, una unidad que no llegó allá, y al mes son varios cientos de euros pagados por producto que nunca entró en tu cocina. Si nunca pesas nada, nunca lo vas a saber: el proveedor no te va a llamar para decirte que te ha cobrado de más.',
                            )}
                        </p>
                    </div>

                    <h2 id='precios' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.precios'), 'Cómo detectar antes las subidas de precio de tus proveedores')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('lead.precios'),
                                    'Revisar los albaranes con regularidad es lo que permite detectar una subida de precio antes de que dañe el margen de la carta.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.precios.1'),
                                'Si revisas albaranes de forma habitual, te será mucho más fácil pillar cuándo un proveedor te sube un precio sin avisar. Ese medio euro que sube el kilo de un producto, multiplicado por los kilos que compras al mes, se te come el margen sin que lo notes en caja.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.precios.2'),
                                'Eso sí, hacerlo de cabeza, producto a producto y proveedor a proveedor, es imposible. Y meterlo todo en un Excel tampoco es plan para nadie que tiene que estar detrás de los fogones. Para esto, mejor apoyarte en herramientas del mercado que lo hagan por ti.',
                            )}
                        </p>
                    </div>

                    <h2 id='pagar' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.pagar'), 'Quién controla el albarán, controla lo que paga a cada proveedor')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('lead.pagar'),
                                    'Tener los albaranes ordenados es lo que te permite exigir a un proveedor que corrija una devolución mal gestionada, en lugar de pagarla sin darte cuenta.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.pagar'),
                                'Quien controla sus albaranes controla lo que tiene que pagar a cada proveedor, ni un euro de más. Luego resulta que los programas de los proveedores «siempre fallan» y te acaban cobrando de más porque hay una devolución que nunca se llegó a gestionar bien. Con el albarán en la mano, esa conversación la ganas tú, no ellos.',
                            )}
                        </p>
                    </div>

                    <h2 id='caso' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.caso'), 'Un ejemplo con números (sin inventar magia)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.caso.1'),
                                'Ilustración: compras 40 kg de un producto a 6,50 €/kg. El albarán marca 40 kg. En báscula entran 38,2 kg. La diferencia es 1,8 kg × 6,50 € = 11,70 € en esa entrega.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.caso.2'),
                                'Si eso pasa dos veces por semana en un solo proveedor, hablamos de unos 94 € al mes y más de 1.100 € al año en producto que pagaste y no cocinaste. Multiplica por varios proveedores y el «detalle» deja de ser detalle.',
                            )}
                        </p>
                        <p className={cn(articleUi.pItalic, articleUi.pGap2xl)}>
                            {t(
                                k('p.caso.note'),
                                'Cifras ilustrativas a partir de un desfase de peso habitual en entregas a granel. No es el dato de un cliente concreto.',
                            )}
                        </p>
                    </div>

                    <h2 id='materia-prima' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.materiaPrima'), 'El control de albaranes y tu coste de materia prima')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(
                                    k('lead.materiaPrima'),
                                    'El control de albaranes es la base del dato más importante de tu cuenta de explotación: el coste real de materia prima.',
                                )}
                            </span>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.materiaPrima.1Before'),
                                'Si controlas a todos tus proveedores, controlas tu coste de materia prima. Y ese es el número que decide si tu negocio te está dando lo que te mereces por el esfuerzo de cada día, o si alguien se lo está comiendo por el camino sin que te enteres. No hace falta que sea un susto a final de mes: con el control adecuado, ese dato lo tienes claro cada día, no cuando ya no hay nada que hacer. Encaja con saber ',
                            )}
                            <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                {t(k('linkEscandallo'), 'hacer un escandallo')}
                            </LearnArticleLink>
                            {t(k('p.materiaPrima.1Mid'), ' y calcular el ')}
                            <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                {t(k('linkFoodCost'), 'food cost y margen bruto')}
                            </LearnArticleLink>
                            {t(k('p.materiaPrima.1After'), ' de cada plato.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.materiaPrima.2Before'),
                                'Si llevar todo esto a mano, con pinzas, báscula y memoria, no es lo tuyo (y no tiene por qué serlo, bastante tienes ya con sacar el servicio adelante), prueba ',
                            )}
                            <TipiLink />
                            {t(k('p.materiaPrima.2After'), '. Te lo vas a agradecer.')}
                        </p>
                    </div>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Lo que el control de albaranes no arregla')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'No sustituye el escandallo ni el food cost. Te asegura que el precio y la cantidad que entran son los que pagas. Si luego cocina porciona de más, el agujero sigue en otro sitio.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'Tampoco todo se puede pesar en el momento del servicio: prioriza género caro, fresco y a granel. El resto, muestreo y revisión de precios. Perfecto es el enemigo de hecho esta semana.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('closing.signoff'),
                                'Pesa la próxima entrega cara. El Excel puede esperar. Ese kilo de menos, no.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Quieres controlar tus albaranes sin pelearte con el Excel?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi centralizas entregas, precios de proveedores y el impacto en tu margen. Cuéntanos cómo lo llevas y lo miramos juntos.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        <li>
                            {t(
                                k('sources.1'),
                                'Ejemplo de desfase de peso (40 kg facturados vs 38,2 kg recibidos a 6,50 €/kg): ilustración propia de Tipi. No es el dato de un local concreto.',
                            )}
                        </li>
                        <li>
                            {t(
                                k('sources.2'),
                                'El albarán como documento de entrega frente a pedido y factura es práctica operativa habitual en hostelería; el artículo no cita una norma única que fije el procedimiento de pesaje.',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
